import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PrefillIcon from '@mui/icons-material/PlaylistAddCheck';
import ProvenanceBadge from './ProvenanceBadge';
import PrimaryLicenseChip from './PrimaryLicenseChip';
import DisclaimerNote from './DisclaimerNote';
import { hideHoursBasedEthics } from './ethicsDisplay';
import SubjectRequirementsList from './SubjectRequirementsList';
import CreditLimitsList from './CreditLimitsList';
import StateReferenceAccordion from './StateReferenceAccordion';
import { BOARD_AUTHORITY_DISCLAIMER } from './disclaimers';
import licenseComplianceService from '../../services/licenseComplianceService';
import type { ComplianceMode, LicenseSetupEntryDto } from '../../services/licenseComplianceService';
import userService from '../../services/userService';
import type { UserLicenseDto } from '../../services/userService';
import { useUserContext } from '../../hooks/useUserContext';
import { getApiErrorInfo } from '../../services/api';
import { formatUtcDate } from '../../utils/dateTime';
import { newLicensePulseSx } from './newLicensePulse';

interface LicenseRequirementCardProps {
  entry: LicenseSetupEntryDto;
  /** Called after a successful save/confirm so the parent can refresh state. */
  onSaved: () => void | Promise<void>;
  /** Joined license record, when the parent has it (enables the Active chip and expiration date). */
  license?: UserLicenseDto | null;
  /** When set, renders an edit-license icon button in the card header. */
  onEditLicense?: () => void;
  /** When set, renders a delete-license icon button in the card header. */
  onDeleteLicense?: () => void;
  /** Collapse confirmed cards to a compact summary row with an expand chevron. */
  collapsible?: boolean;
  /** Render the board-authority disclaimer inside the card. Pages that show one shared disclaimer pass false. */
  showDisclaimer?: boolean;
  /** Play the just-added attention pulse (ring + tint fade) on the card. */
  highlight?: boolean;
  /**
   * Display name of the user's primary (principal place of business) license
   * state, resolved by the parent from the setup list. Names the home state in
   * the reciprocity election copy.
   */
  primaryStateLabel?: string | null;
}

function toDateInput(iso?: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** Today's UTC calendar date (yyyy-mm-dd), matching how period boundaries are stored. */
function todayUtcDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatPeriodType(type?: string | null): string | null {
  switch (type) {
    case 'calendar_based':
      return 'Calendar-year based reporting period';
    case 'birthdate_based':
      return 'Reporting period anchored to your birth date';
    case 'license_date_based':
      return 'Reporting period anchored to your license issue date';
    default:
      return null;
  }
}

/**
 * Per-license card: license identity in the header (state, chips, provenance,
 * optional license edit/delete actions) with the CPE requirement setup below.
 * Prefilled values can be confirmed as-is ("Defaults confirmed") or edited
 * ("Custom requirements"). Unedited dates are sent back with their original
 * timestamps so the server's prefill-match detection stays exact.
 */
export default function LicenseRequirementCard({
  entry,
  onSaved,
  license = null,
  onEditLicense,
  onDeleteLicense,
  collapsible = false,
  showDisclaimer = true,
  highlight = false,
  primaryStateLabel = null,
}: LicenseRequirementCardProps) {
  const requirement = entry.requirement;
  const { refreshUser } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Elected home-state reciprocity replaces local setup entirely: the card
  // shows the mirroring summary instead of prefill/form/subject/limit sections.
  const reciprocityMode = entry.complianceMode === 'home_state_reciprocity';
  const [electDialogOpen, setElectDialogOpen] = useState(false);
  // Explicit consent: the user must acknowledge the state's reciprocity rules
  // before the Elect button enables. Reset on every dialog open.
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const [switchModeError, setSwitchModeError] = useState<string | null>(null);
  // Manual-ethics states (specific attested course, no hours target) hide the
  // hours-based ethics field entirely — "0 ethics hours" would misread as "no
  // ethics requirement". Keyed off the server value, not form state, so the
  // field can't vanish mid-edit.
  const hideEthics = hideHoursBasedEthics(requirement?.ethicsHours, entry.subjectRequirements);

  // Anchor rescue prompt + "Estimated" treatment only render while unconfirmed:
  // once the user modifies or confirms, their reviewed window is authoritative.
  const missingAnchor = entry.status === 'unconfirmed' ? entry.missingPeriodAnchor ?? null : null;
  const [birthDateInput, setBirthDateInput] = useState('');
  const [savingBirthDate, setSavingBirthDate] = useState(false);
  const [birthDateError, setBirthDateError] = useState<string | null>(null);

  const [totalHours, setTotalHours] = useState<string>(String(requirement?.totalHours ?? 0));
  const [ethicsHours, setEthicsHours] = useState<string>(String(requirement?.ethicsHours ?? 0));
  const [periodStart, setPeriodStart] = useState<string>(toDateInput(requirement?.periodStart));
  const [periodEnd, setPeriodEnd] = useState<string>(toDateInput(requirement?.periodEnd));

  // Reseed the form when the server-side requirement values actually change
  // (prefill, reset, save elsewhere). Comparing by value — not object identity
  // or status — preserves in-progress edits across unrelated refreshes.
  const seedKey = requirement
    ? `${requirement.totalHours}|${requirement.ethicsHours}|${requirement.periodStart}|${requirement.periodEnd}`
    : 'none';
  const [lastSeedKey, setLastSeedKey] = useState(seedKey);
  if (seedKey !== lastSeedKey) {
    setLastSeedKey(seedKey);
    setTotalHours(String(requirement?.totalHours ?? 0));
    setEthicsHours(String(requirement?.ethicsHours ?? 0));
    setPeriodStart(toDateInput(requirement?.periodStart));
    setPeriodEnd(toDateInput(requirement?.periodEnd));
  }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Confirmed requirements render read-only until the user explicitly re-enters
  // edit mode, so a confirmed card visibly "locks in" and can't be nudged by accident.
  const [editMode, setEditMode] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);

  const totalNum = Number(totalHours);
  const ethicsNum = Number(ethicsHours);

  const validationError = useMemo(() => {
    // Credits increment in tenths — validate on the raw strings to avoid
    // IEEE 754 false rejections from float arithmetic.
    const tenths = /^\d+(\.\d)?$/;
    if (!Number.isFinite(totalNum) || totalNum < 0) return 'Total hours must be zero or more.';
    if (!tenths.test(totalHours.trim())) return 'Total hours must be in increments of 0.1.';
    if (!Number.isFinite(ethicsNum) || ethicsNum < 0) return 'Ethics hours must be zero or more.';
    if (!tenths.test(ethicsHours.trim())) return 'Ethics hours must be in increments of 0.1.';
    if (ethicsNum > totalNum) return 'Ethics hours cannot exceed total hours.';
    if (!periodStart || !periodEnd) return 'Both period dates are required.';
    if (periodEnd <= periodStart) return 'Period end must be after period start.';
    // A period that has already ended can't be the one being tracked — today
    // should fall inside it (a not-yet-started period is allowed, with a warning).
    if (periodEnd < todayUtcDate()) {
      return 'This reporting period has already ended. Enter your current reporting period.';
    }
    return null;
  }, [totalNum, ethicsNum, totalHours, ethicsHours, periodStart, periodEnd]);

  // Non-blocking: a period that hasn't started yet is legitimate (e.g. set up
  // ahead of a new cycle) but worth flagging so a typo'd year doesn't slip by.
  const formPeriodIsFuture = !validationError && periodStart > todayUtcDate();
  const savedPeriodIsFuture =
    !!requirement && toDateInput(requirement.periodStart) > todayUtcDate();

  const isEdited =
    !!requirement &&
    (totalNum !== requirement.totalHours ||
      ethicsNum !== requirement.ethicsHours ||
      periodStart !== toDateInput(requirement.periodStart) ||
      periodEnd !== toDateInput(requirement.periodEnd));

  const isConfirmed = entry.status === 'confirmed' || entry.status === 'modified';
  const showForm = !isConfirmed || editMode;
  // A fresh manual-entry card (no requirement row, nothing typed yet) shouldn't
  // open with a validation error; the disabled save button is signal enough.
  const pristineManualEntry = !requirement && !periodStart && !periodEnd;

  // Confirmed cards rest collapsed (when the parent opts in); a card that still
  // needs action is always expanded. A status change (confirm, batch confirm)
  // resets any manual toggle so freshly confirmed cards collapse on their own.
  const canCollapse = collapsible && (isConfirmed || reciprocityMode) && !editMode;
  const [expandOverride, setExpandOverride] = useState<boolean | null>(null);
  const [lastStatus, setLastStatus] = useState(entry.status);
  if (entry.status !== lastStatus) {
    setLastStatus(entry.status);
    setExpandOverride(null);
  }
  const expanded = !canCollapse || (expandOverride ?? false);

  // Prefer the full NASBA reference text (every section of the state page);
  // the four excerpt fields are a fallback for states without parsed content.
  const referenceItems: Array<{ label: string; value: string }> =
    entry.referenceSections && entry.referenceSections.length > 0
      ? entry.referenceSections.map((s) => ({ label: s.label, value: s.text }))
      : [
          entry.otherSubjectAreaRequirements
            ? { label: 'Other subject area requirements', value: entry.otherSubjectAreaRequirements }
            : null,
          entry.creditLimitationsText
            ? { label: 'Credit limitations', value: entry.creditLimitationsText }
            : null,
          entry.creditCalculationText
            ? { label: 'Credit calculation', value: entry.creditCalculationText }
            : null,
          entry.otherPoliciesText ? { label: 'Other policies', value: entry.otherPoliciesText } : null,
        ].filter((item): item is { label: string; value: string } => item !== null);

  const periodTypeText = formatPeriodType(entry.reportingPeriodType);
  const stateLabel = entry.stateName || entry.stateCode;

  async function handleSave() {
    if (validationError) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Preserve original timestamps for unedited dates so the backend's
      // exact prefill comparison isn't tripped by date-only round-tripping.
      // With no requirement row yet (manual entry), the save creates one.
      const startPayload =
        requirement && periodStart === toDateInput(requirement.periodStart)
          ? requirement.periodStart
          : `${periodStart}T00:00:00Z`;
      const endPayload =
        requirement && periodEnd === toDateInput(requirement.periodEnd)
          ? requirement.periodEnd
          : `${periodEnd}T23:59:59Z`;

      await licenseComplianceService.updateRequirement(entry.licenseId, {
        totalHours: totalNum,
        ethicsHours: ethicsNum,
        periodStart: startPayload,
        periodEnd: endPayload,
        confirm: true,
        // carryoverHours is intentionally never sent from this surface: the
        // omitted field leaves the stored value untouched. Carry-over is
        // managed entirely on the Reporting page (apply / exclude).
      });
      await onSaved();
      setEditMode(false);
    } catch (error) {
      setSaveError(getApiErrorInfo(error).message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    setTotalHours(String(requirement?.totalHours ?? 0));
    setEthicsHours(String(requirement?.ethicsHours ?? 0));
    setPeriodStart(toDateInput(requirement?.periodStart));
    setPeriodEnd(toDateInput(requirement?.periodEnd));
    setSaveError(null);
    setEditMode(false);
  }

  async function handleSaveBirthDate() {
    if (!birthDateInput) return;
    setSavingBirthDate(true);
    setBirthDateError(null);
    try {
      // The backend re-prefills every unconfirmed birthdate-anchored window
      // when the birth date changes, so a single refresh picks them all up.
      await userService.updateMyProfileDetails({ birthDate: birthDateInput });
      await onSaved();
      void refreshUser();
    } catch (error) {
      setBirthDateError(getApiErrorInfo(error).message);
    } finally {
      setSavingBirthDate(false);
    }
  }

  async function handleSetComplianceMode(mode: ComplianceMode) {
    setSwitchingMode(true);
    setSwitchModeError(null);
    try {
      await licenseComplianceService.setComplianceMode(entry.licenseId, mode);
      await onSaved();
      setElectDialogOpen(false);
    } catch (error) {
      setSwitchModeError(getApiErrorInfo(error).message);
    } finally {
      setSwitchingMode(false);
    }
  }

  async function handlePrefill() {
    setPrefilling(true);
    setPrefillError(null);
    try {
      await licenseComplianceService.prefillLicense(entry.licenseId);
      await onSaved();
    } catch (error) {
      setPrefillError(getApiErrorInfo(error).message);
    } finally {
      setPrefilling(false);
    }
  }

  const expirationDate = license?.expirationDate ?? null;

  return (
    <Card variant="outlined" sx={highlight ? newLicensePulseSx : undefined}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header: license identity + provenance + actions */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: expanded ? 2 : 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {stateLabel}
              </Typography>
              {entry.isPrimary && <PrimaryLicenseChip />}
              {license && (
                <Chip
                  label={license.isActive ? 'Active' : 'Inactive'}
                  size="small"
                  sx={license.isActive
                    ? { bgcolor: 'status.completed.bg', color: 'status.completed.fg', fontWeight: 500 }
                    : { bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg', fontWeight: 500 }}
                />
              )}
              {/* Under reciprocity the provenance ladder doesn't apply — the
                  election is the whole setup, so its own chip replaces the badge. */}
              {reciprocityMode ? (
                <Chip
                  label="Home-State Reciprocity"
                  size="small"
                  sx={{ bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg', fontWeight: 500 }}
                />
              ) : (
                <ProvenanceBadge status={isEdited ? 'modified' : entry.status} stateCode={entry.stateCode} />
              )}
              {missingAnchor && requirement && (
                <Tooltip
                  title={
                    missingAnchor === 'birth_date'
                      ? `${stateLabel} anchors reporting periods to your birth date, which you haven't provided, so this window is a calendar-based estimate.`
                      : `${stateLabel} anchors reporting periods to your license issue date, which this license doesn't have, so this window is a calendar-based estimate.`
                  }
                >
                  <Chip
                    label="Estimated"
                    size="small"
                    sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg', fontWeight: 500 }}
                  />
                </Tooltip>
              )}
              {savedPeriodIsFuture && (
                <Tooltip
                  title={`This reporting period doesn't start until ${formatUtcDate(requirement.periodStart)}. Make sure that's intentional; today's date isn't inside the period being tracked.`}
                >
                  <Chip
                    label="Future period"
                    size="small"
                    sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg', fontWeight: 500 }}
                  />
                </Tooltip>
              )}
              {entry.stateDataUpdatedSinceReview && (
                <Tooltip title={`${stateLabel}'s state requirement data changed after you last reviewed this license.`}>
                  <Chip
                    label="State data updated"
                    size="small"
                    sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg', fontWeight: 500 }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {entry.licenseNumber ? `License ${entry.licenseNumber}` : 'CPA license'}
            </Typography>
            {(entry.issueDate || expirationDate) && (
              <Typography variant="caption" color="text.secondary">
                {entry.issueDate && `Issued: ${formatUtcDate(entry.issueDate)}`}
                {entry.issueDate && expirationDate && ' · '}
                {expirationDate && `Expires: ${formatUtcDate(expirationDate)}`}
              </Typography>
            )}
            {/* Collapsed resting state: the ledger line */}
            {!expanded && reciprocityMode ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Satisfied via home-state reciprocity
                {primaryStateLabel ? ` · mirrors ${primaryStateLabel}` : ''}
              </Typography>
            ) : !expanded && requirement ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {requirement.totalHours} total hrs ·{' '}
                {!hideEthics && `${requirement.ethicsHours} ethics · `}
                {formatUtcDate(requirement.periodStart)} – {formatUtcDate(requirement.periodEnd)}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {onEditLicense && (
              <Tooltip title="Edit license">
                <IconButton size="small" aria-label="Edit license" onClick={onEditLicense}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDeleteLicense && (
              <Tooltip title="Delete license">
                <IconButton
                  size="small"
                  aria-label="Delete license"
                  onClick={onDeleteLicense}
                  sx={{ '&:hover': { color: 'error.main' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canCollapse && (
              <IconButton
                size="small"
                aria-label={expanded ? `Collapse ${stateLabel} requirements` : `Expand ${stateLabel} requirements`}
                aria-expanded={expanded}
                onClick={() => setExpandOverride(!expanded)}
              >
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
                  }}
                />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Elected reciprocity: the mirroring summary replaces every local
            setup section — prefill, hours form, subjects, credit limits. */}
        {expanded && reciprocityMode && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Satisfied via home-state reciprocity
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {primaryStateLabel
                  ? `${stateLabel} accepts CPE from your principal place of business, so your ${primaryStateLabel} compliance satisfies this license.`
                  : `${stateLabel} accepts CPE compliance from your principal place of business state.`}
              </Typography>
              {entry.ethicsCarveoutApplies && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {stateLabel} still requires its own ethics (hours or a board-approved course),
                  tracked on the Reporting page.
                </Typography>
              )}
              {entry.reciprocityNotesText && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {entry.reciprocityNotesText}
                </Typography>
              )}
              {switchModeError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {switchModeError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => void handleSetComplianceMode('standard')}
                  disabled={switchingMode}
                  startIcon={switchingMode ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  Switch to standard tracking
                </Button>
                {entry.reciprocityElectedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Elected {formatUtcDate(entry.reciprocityElectedAt)}
                  </Typography>
                )}
              </Box>
            </Box>

            <StateReferenceAccordion items={referenceItems} revisionDate={entry.stateDataRevisionDate} />

            {showDisclaimer && <DisclaimerNote sx={{ mt: 2 }}>{BOARD_AUTHORITY_DISCLAIMER}</DisclaimerNote>}
          </>
        )}

        {expanded && !reciprocityMode && (
          <>
            {periodTypeText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {periodTypeText}
              </Typography>
            )}

            {missingAnchor === 'birth_date' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {stateLabel} calculates your reporting period from your birth date. Add it to
                  replace the estimated window with your actual one.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    label="Birth date"
                    type="date"
                    size="small"
                    value={birthDateInput}
                    onChange={(e) => setBirthDateInput(e.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                      // Backend rejects birth dates outside this range (see UserService).
                      htmlInput: { min: '1900-01-01', max: new Date().toISOString().substring(0, 10) },
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveBirthDate}
                    disabled={!birthDateInput || savingBirthDate}
                    startIcon={savingBirthDate ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    Save
                  </Button>
                </Box>
                {birthDateError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                    {birthDateError}
                  </Typography>
                )}
              </Alert>
            )}
            {missingAnchor === 'issue_date' && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  onEditLicense && (
                    <Button color="inherit" size="small" onClick={onEditLicense}>
                      Edit license
                    </Button>
                  )
                }
              >
                {stateLabel} calculates your reporting period from your license issue date. Add
                the issue date to this license to replace the estimated window with your actual one.
              </Alert>
            )}

            {entry.stateDataUpdatedSinceReview && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {stateLabel}'s CPE requirement data changed since your last review. Reconfirm
                the values below to keep your tracking current.
              </Alert>
            )}

            {!requirement && !entry.hasStateData && (
              <Alert severity="info" sx={{ mb: 2 }}>
                We don't have CPE requirement data for {stateLabel} yet.
                Enter the requirements from your state board below and we'll track your progress
                against them.
              </Alert>
            )}
            {!requirement && entry.hasStateData ? (
              <>
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  No requirement data is loaded for this license yet. Load your state's default
                  requirements, then review and confirm them.
                </Alert>
                {prefillError && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {prefillError}
                  </Alert>
                )}
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handlePrefill}
                    disabled={prefilling}
                    startIcon={prefilling ? <CircularProgress size={16} color="inherit" /> : <PrefillIcon />}
                  >
                    {prefilling ? 'Loading…' : 'Load state requirements'}
                  </Button>
                </Box>
              </>
            ) : requirement && !showForm ? (
              <>
                {/* Confirmed requirements: read-only summary + explicit edit affordance */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  {[
                    { label: 'Total hours', value: String(requirement.totalHours) },
                    ...(hideEthics
                      ? []
                      : [{ label: 'Ethics hours', value: String(requirement.ethicsHours) }]),
                    { label: 'Period start', value: formatUtcDate(requirement.periodStart) },
                    { label: 'Period end', value: formatUtcDate(requirement.periodEnd) },
                  ].map((item: { label: string; value: string }) => (
                    <Box key={item.label}>
                      <Typography
                        variant="overline"
                        sx={{ color: 'text.secondary', lineHeight: 1.5, display: 'block' }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="body2">{item.value}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    Edit requirements
                  </Button>
                  {requirement.confirmedAt && (
                    <Typography variant="caption" color="text.secondary">
                      Confirmed {formatUtcDate(requirement.confirmedAt)}
                    </Typography>
                  )}
                </Box>
              </>
            ) : (
              <>
                {/* Editable fields */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="Total hours"
                    type="number"
                    size="small"
                    value={totalHours}
                    onChange={(e) => setTotalHours(e.target.value)}
                    slotProps={{ htmlInput: { min: 0, step: 1 } }}
                  />
                  {!hideEthics && (
                    <TextField
                      label="Ethics hours"
                      type="number"
                      size="small"
                      value={ethicsHours}
                      onChange={(e) => setEthicsHours(e.target.value)}
                      slotProps={{ htmlInput: { min: 0, step: 1 } }}
                    />
                  )}
                  <TextField
                    label="Period start"
                    type="date"
                    size="small"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    label="Period end"
                    type="date"
                    size="small"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Box>

                {validationError && !pristineManualEntry && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                    {validationError}
                  </Typography>
                )}
                {formPeriodIsFuture && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 1, color: 'status.modified.fg' }}
                  >
                    This reporting period starts in the future. You can save it, but make sure
                    it's the period you intend to track.
                  </Typography>
                )}
                {saveError && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    {saveError}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !!validationError}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {!requirement
                      ? 'Save requirements'
                      : isEdited
                        ? 'Save custom requirements'
                        : 'Confirm requirements'}
                  </Button>
                  {isConfirmed && (
                    <Button onClick={handleCancelEdit} disabled={saving}>
                      Cancel
                    </Button>
                  )}
                  {entry.sourceStateDataRevisionDate && (
                    <Typography variant="caption" color="text.secondary">
                      State data last revised {formatUtcDate(entry.sourceStateDataRevisionDate)}
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {/* Subject requirements (per-category minimums that recur every period) */}
            {requirement && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <SubjectRequirementsList entry={entry} onSaved={onSaved} />
              </Box>
            )}

            {/* Credit limits (delivery-method caps, e.g. max self-study hours) */}
            {requirement && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <CreditLimitsList entry={entry} onSaved={onSaved} />
              </Box>
            )}

            {/* Home-state reciprocity election. Only two cases surface it:
                currently eligible, or blocked solely on the primary's setup
                (an actionable hint). Every other blocked reason stays silent —
                no reciprocity hints on states that don't offer it. */}
            {(entry.reciprocityEligible || entry.reciprocityBlockedReason === 'primary_not_set_up') && (
              <Box sx={{ mb: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Home-state reciprocity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stateLabel} lets you satisfy its CPE requirements by meeting the requirements
                  of your principal place of business
                  {primaryStateLabel ? ` (${primaryStateLabel})` : ''}.
                </Typography>
                {entry.reciprocityBlockedReason === 'primary_not_set_up' ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Set up your {primaryStateLabel ?? 'primary license'} requirements first to use
                    this option.
                  </Typography>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSwitchModeError(null);
                      setRulesAcknowledged(false);
                      setElectDialogOpen(true);
                    }}
                    sx={{ mt: 1.5 }}
                  >
                    Satisfy via home-state reciprocity
                  </Button>
                )}
              </Box>
            )}

            <StateReferenceAccordion items={referenceItems} revisionDate={entry.stateDataRevisionDate} />

            {showDisclaimer && <DisclaimerNote sx={{ mt: 2 }}>{BOARD_AUTHORITY_DISCLAIMER}</DisclaimerNote>}
          </>
        )}
      </CardContent>

      {/* Attestation confirmation — election replaces local setup, so it gets
          an explicit consent step (mirrors the manual-requirement pattern). */}
      <Dialog
        open={electDialogOpen}
        onClose={switchingMode ? undefined : () => setElectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Satisfy {stateLabel} via home-state reciprocity</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {stateLabel} accepts CPE compliance from your principal place of business
            {primaryStateLabel ? ` (${primaryStateLabel})` : ''}. While elected, this license's
            compliance status mirrors your {primaryStateLabel ?? 'primary'} license instead of
            tracking {stateLabel} hours separately.
          </Typography>
          {entry.ethicsCarveoutApplies && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {stateLabel} still requires its own ethics (hours or a board-approved course),
              tracked on the Reporting page.
            </Typography>
          )}
          {entry.reciprocityNotesText && (
            <Box sx={{ bgcolor: 'tint.primarySoft', borderRadius: 1, px: 1.5, py: 1.25, mb: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                {stateLabel} reciprocity rules
              </Typography>
              <Typography variant="body2">{entry.reciprocityNotesText}</Typography>
            </Box>
          )}
          <Typography variant="body2">
            By continuing you attest that your principal place of business is in{' '}
            {primaryStateLabel ?? 'your primary license state'} and that you meet its CPE
            requirements. Any existing {stateLabel} setup isn't deleted; switching back to
            standard tracking restores it.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1.5, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
            control={
              <Checkbox
                checked={rulesAcknowledged}
                onChange={(e) => setRulesAcknowledged(e.target.checked)}
                disabled={switchingMode}
              />
            }
            label={
              <Typography variant="body2">
                I have read and understand {stateLabel}&apos;s reciprocity rules
              </Typography>
            }
          />
          {switchModeError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {switchModeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setElectDialogOpen(false)} disabled={switchingMode}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSetComplianceMode('home_state_reciprocity')}
            disabled={switchingMode || !rulesAcknowledged}
            startIcon={switchingMode ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Elect reciprocity
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
